import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { IconType } from 'react-icons'
import {
  FiHome, FiCompass, FiTarget, FiUsers, FiGift, FiBookmark, FiPlus,
  FiCheckCircle, FiSettings, FiBarChart2, FiClipboard, FiUserCheck,
  FiBook, FiAward, FiPackage, FiMap, FiFolder, FiTrendingUp, FiDroplet, FiFlag,
  FiDollarSign, FiHeart, FiMessageSquare, FiCalendar, FiShare2, FiLink,
  FiFileText, FiLayers, FiActivity,
} from 'react-icons/fi'
import { LuSchool } from 'react-icons/lu'
import {
  useAuthStore, useCommunityStore, useChallengeStore, useGiftStore, useNotificationStore,
  selectUserRole, selectHasSchool, selectIsSchoolContext,
  selectActiveContext, selectHasLinkedChildren,
  selectMyChallengeStats, selectPendingGiftCount, selectUnreadCount, selectNotificationCounts,
} from '../../../store'
import type { UserRole } from '../../../types'
import { getAdminCategories } from './adminNavData'
import {
  isPlatformAdmin as isPlatformAdminHelper,
  isPlatformStaff as isPlatformStaffHelper,
  isSchoolAdmin as isSchoolAdminHelper,
  isVendor as isVendorHelper,
  hasContext,
  getRoleInContext,
} from '../../../lib/contextHelpers'

export interface NavItemData {
  to: string
  icon: IconType
  label: string
  external?: boolean
  badgeCount?: number
}

export interface NavSubSection {
  title: string
  icon: IconType
  items: NavItemData[]
  emptyMessage?: string
}

export interface NavSection {
  id: string
  title: string
  items: NavItemData[]
  isCollapsible?: boolean
  subSections?: NavSubSection[]
  overflowLink?: NavItemData
  badgeCount?: number
}

const roleLabels: Record<UserRole, string> = {
  user: 'User',
  platform_admin: 'Platform Admin',
  head_school_admin: 'Head Admin',
  school_admin: 'School Admin',
  teacher: 'Teacher',
  student: 'Student',
  parent: 'Parent',
  vendor: 'Vendor',
  staff: 'Staff',
}

export function useNavigationItems() {
  const { t } = useTranslation()
  const user = useAuthStore((s) => s.user)
  const role = useAuthStore(selectUserRole)
  const hasSchool = useAuthStore(selectHasSchool)
  const isSchoolContext = useAuthStore(selectIsSchoolContext)
  const activeContext = useAuthStore(selectActiveContext)
  const isParentUser = useAuthStore(selectHasLinkedChildren)

  const myFullSchoolCommunity = useCommunityStore((s) => s.myFullSchoolCommunity)
  const myGradeCommunity = useCommunityStore((s) => s.myGradeCommunity)
  const myClassCommunity = useCommunityStore((s) => s.myClassCommunity)
  const personalCommunities = useCommunityStore((s) => s.personalCommunities)

  // Badge counts for nav indicators
  const challengeStats = useChallengeStore(selectMyChallengeStats)
  const pendingGiftCount = useGiftStore(selectPendingGiftCount)
  const unreadNotifCount = useNotificationStore(selectUnreadCount)
  const notifCounts = useNotificationStore(selectNotificationCounts)

  // Per-section unread counts from notification categories
  const communityUnread = (notifCounts.byCategory?.community_activity ?? 0) + (notifCounts.byCategory?.social_activity ?? 0)
  const verificationUnread = notifCounts.byCategory?.verification ?? 0
  const teacherFeedbackUnread = notifCounts.byCategory?.teacher_feedback ?? 0
  const gamificationUnread = notifCounts.byCategory?.gamification ?? 0

  useEffect(() => {
    const store = useCommunityStore.getState()
    if (hasSchool && user?.schoolId) {
      store.fetchMySchoolCommunityByLevel(user.schoolId, 'full_school')
      store.fetchMySchoolCommunityByLevel(user.schoolId, 'grade', '5th Grade')
      store.fetchMySchoolCommunityByLevel(user.schoolId, 'class', undefined, 'class-5a')
    }
    store.fetchPersonalCommunities()
  }, [hasSchool, user?.schoolId])

  const schoolCommunities = [
    myClassCommunity, myGradeCommunity, myFullSchoolCommunity,
  ].filter(Boolean) as NonNullable<typeof myFullSchoolCommunity>[]

  // Context-based role checks
  const isPlatformAdmin = user ? isPlatformAdminHelper(user) : false
  const isStaff = user ? isPlatformStaffHelper(user) : false
  const isSchoolAdmin = user ? isSchoolAdminHelper(user) : false
  const isTeacher = user ? getRoleInContext(user, 'school') === 'teacher' : false
  const isStudent = user ? getRoleInContext(user, 'school') === 'student' : false
  const isParent = isParentUser
  const isVendor = user ? isVendorHelper(user) : false

  const canManageSchool = isSchoolAdmin
  const showPersonalSections = !isVendor && (!isSchoolContext || isSchoolAdmin || isTeacher || isStudent || isParent)
  const canSeeSchoolSection = isSchoolContext
    ? (isSchoolAdmin || isTeacher || (hasSchool && (isStudent || isParent)))
    : (isSchoolAdmin || isTeacher || (hasSchool && isStudent))

  // Build sections — only include visible ones
  const sections: NavSection[] = []

  // General — context-aware home link
  const generalItems: NavItemData[] = []
  switch (activeContext) {
    case 'school':
      generalItems.push({ to: '/school', icon: LuSchool, label: t('nav.schoolDashboard') })
      break
    case 'business':
      generalItems.push({ to: '/vendor', icon: FiHome, label: t('nav.vendorDashboard', 'Vendor Dashboard') })
      break
    case 'platform':
      generalItems.push({ to: '/admin', icon: FiHome, label: t('nav.adminHome', 'Admin Home') })
      break
    case 'personal':
    default:
      generalItems.push(
        { to: '/', icon: FiHome, label: t('nav.dashboard') },
        { to: '/explore', icon: FiCompass, label: t('nav.explore') },
      )
      break
  }
  sections.push({
    id: 'general',
    title: t('nav.general'),
    items: generalItems,
  })

  // Challenges (personal sections only)
  if (showPersonalSections) {
    sections.push({
      id: 'challenges',
      title: t('nav.challenges'),
      items: [
        { to: '/my-challenges', icon: FiTarget, label: t('nav.myChallenges'), badgeCount: challengeStats.inProgress || undefined },
        { to: '/saved', icon: FiBookmark, label: t('nav.saved') },
        { to: '/learning-paths', icon: FiBook, label: 'Learning Paths' },
        { to: '/portfolios', icon: FiFolder, label: 'Portfolio' },
      ],
    })
  }

  // Communities — filtered by active context (hidden in business/platform)
  if (activeContext === 'personal' || activeContext === 'school') {
    const commSubSections: NavSubSection[] = []

    if (activeContext === 'school') {
      // School context: only school communities
      if (hasSchool && schoolCommunities.length > 0) {
        commSubSections.push({
          title: 'School',
          icon: LuSchool,
          items: schoolCommunities.map(c => ({ to: `/communities/${c.id}`, icon: FiUsers, label: c.name })),
        })
      }
    } else {
      // Personal context: private first, school second
      commSubSections.push({
        title: 'Private',
        icon: FiUsers,
        items: personalCommunities.slice(0, 5).map(c => ({ to: `/communities/${c.id}`, icon: FiUsers, label: c.name })),
        emptyMessage: 'No communities yet',
      })
      if (hasSchool && schoolCommunities.length > 0) {
        commSubSections.push({
          title: 'School',
          icon: LuSchool,
          items: schoolCommunities.map(c => ({ to: `/communities/${c.id}`, icon: FiUsers, label: c.name })),
        })
      }
    }

    sections.push({
      id: 'communities',
      title: t('nav.myCommunities', 'My Communities'),
      items: [
        { to: '/communities', icon: FiCompass, label: 'All Communities' },
      ],
      isCollapsible: true,
      subSections: commSubSections,
      overflowLink: personalCommunities.length > 5
        ? { to: '/communities/private', icon: FiUsers, label: `View all (${personalCommunities.length})` }
        : undefined,
    })
  }

  // Gifts & Progress (personal sections only)
  if (showPersonalSections) {
    sections.push({
      id: 'gifts',
      title: t('nav.gifts', 'Gifts'),
      items: [{ to: '/gifts', icon: FiGift, label: t('nav.gifts'), badgeCount: pendingGiftCount || undefined }],
    })

    sections.push({
      id: 'progress',
      title: 'Progress',
      items: [
        { to: '/journey-map', icon: FiMap, label: 'Journey Map' },
        { to: '/badges', icon: FiAward, label: 'Badges', badgeCount: gamificationUnread || undefined },
        { to: '/xp-history', icon: FiTrendingUp, label: 'XP History' },
      ],
    })
  }

  // Parenting — consolidated parent features
  if (isParent) {
    sections.push({
      id: 'parenting',
      title: t('nav.parenting', 'Parenting'),
      items: [
        { to: '/parent', icon: FiUserCheck, label: t('nav.parentDashboard', 'Parent Dashboard') },
        { to: '/school/child-grades', icon: FiAward, label: t('nav.childGrades') },
      ],
    })
  }

  // School — sub-grouped by role
  if (canSeeSchoolSection) {
    if (canManageSchool) {
      // School Admin: Administration + Classroom sub-sections
      sections.push({
        id: 'school',
        title: t('nav.school'),
        items: [],
        isCollapsible: true,
        subSections: [
          {
            title: 'Administration',
            icon: FiHome,
            items: [
              { to: '/school', icon: FiHome, label: t('nav.schoolDashboard') },
              { to: '/school/roster', icon: FiUsers, label: t('nav.roster') },
              { to: '/school/classes', icon: FiBook, label: t('nav.classes') },
              { to: '/school/teachers', icon: FiUserCheck, label: t('nav.teachers') },
            ],
          },
          {
            title: 'Classroom',
            icon: FiBarChart2,
            items: [
              { to: '/school/classroom', icon: FiBarChart2, label: 'Classroom Dashboard' },
              { to: '/school/learning-path-designer', icon: FiMap, label: 'Path Designer' },
              { to: '/school/assignments', icon: FiClipboard, label: t('nav.assignments') },
              { to: '/school/reviews', icon: FiCheckCircle, label: t('nav.reviews', 'Reviews') },
              { to: '/school/grades', icon: FiAward, label: t('nav.grades') },
              { to: '/school/surveys', icon: FiMessageSquare, label: 'Surveys' },
              { to: '/school/reflection-analytics', icon: FiActivity, label: 'Reflection Analytics' },
              { to: '/school/purchase-requests', icon: FiDollarSign, label: 'Purchase Requests' },
              { to: '/school/bulk-gift', icon: FiGift, label: 'Bulk Gift' },
              { to: '/school/alumni-transition', icon: FiShare2, label: 'Alumni Transition' },
            ],
          },
        ],
      })
    } else if (isTeacher) {
      // Teacher: My Classroom + Tools sub-sections
      sections.push({
        id: 'school',
        title: t('nav.school'),
        items: [],
        isCollapsible: true,
        subSections: [
          {
            title: 'My Classroom',
            icon: FiBarChart2,
            items: [
              { to: '/school/classroom', icon: FiBarChart2, label: 'Classroom Dashboard' },
              { to: '/school/assignments', icon: FiClipboard, label: t('nav.assignments') },
              { to: '/school/reviews', icon: FiCheckCircle, label: t('nav.reviews', 'Reviews') },
              { to: '/school/grades', icon: FiAward, label: t('nav.grades') },
            ],
          },
          {
            title: 'Tools',
            icon: FiClipboard,
            items: [
              { to: '/school/learning-path-designer', icon: FiMap, label: 'Path Designer' },
              { to: '/school/reflection-analytics', icon: FiActivity, label: 'Reflection Analytics' },
            ],
          },
        ],
      })
    } else if (isStudent) {
      // Student: flat, only 2 items
      sections.push({
        id: 'school',
        title: t('nav.school'),
        items: [
          { to: '/school/my-assignments', icon: FiClipboard, label: t('nav.myAssignments') },
          { to: '/school/my-grades', icon: FiAward, label: t('nav.myGrades') },
        ],
      })
    }
    // Parent no longer appears in School section — consolidated in Parenting above
  }

  // Vendor
  if (isVendor) {
    sections.push({
      id: 'vendor',
      title: t('nav.vendor'),
      items: [
        { to: '/vendor/challenges', icon: FiTarget, label: t('nav.myChallenges') },
        { to: '/vendor/orders', icon: FiPackage, label: t('nav.orders') },
        { to: '/vendor/analytics', icon: FiBarChart2, label: t('nav.analytics') },
        { to: '/vendor/events', icon: FiCalendar, label: 'Events' },
        { to: '/vendor/settings', icon: FiSettings, label: 'Settings' },
      ],
    })
  }

  // Admin — lean sidebar with category headers (full items on AdminHome grid page)
  if (isPlatformAdmin || isStaff) {
    if (isPlatformAdmin) {
      const categories = getAdminCategories(t)
      sections.push({
        id: 'admin',
        title: t('nav.admin'),
        items: categories.map(cat => ({
          to: cat.items.find(i => !i.external)?.to ?? '/admin',
          icon: cat.icon,
          label: cat.title,
        })),
        isCollapsible: true,
      })
    } else {
      // Staff only sees verification queue
      sections.push({
        id: 'admin',
        title: t('nav.staffTools'),
        items: [
          { to: '/admin/verification', icon: FiCheckCircle, label: t('nav.verificationQueue') },
        ],
      })
    }
  }

  return {
    sections,
    role,
    roleLabel: role ? roleLabels[role] : '',
    hasSchool,
  }
}
